import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import templateService, { Template } from "../../services/template.service";
import Loader from "../../widgets/Loader";
import {
  ArrowLeft, ChevronDown,
  FileJson, Type, Box, Calendar, Clock,
  Hash, Sparkles, Code, List,
  Eye,
  Info,
  LayoutTemplate,
} from "lucide-react";
import Breadcrumbs from "../../widgets/Breadcurmbs";

// Helper to format date
const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}
// --- Recursive Property View Component ---
const PropertyView = ({
  propName,
  propData,
  isRequired,
  level = 0,
}: {
  propName: string;
  propData: any;
  isRequired: boolean;
  level?: number;
}) => {
  const isObject = propData?.type === "object" && propData?.properties;
  const isArray = propData?.type === "array" && propData?.items;
  const isExpandable = isObject || isArray;

  const [isExpanded, setIsExpanded] = useState(true);

  // Check if the property has an enum list
  const hasEnum =
    propData.enum && Array.isArray(propData.enum) && propData.enum.length > 0;

  // Determine the type to display in the UI
  const displayType = hasEnum ? "enum" : propData.type;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "object":
        return <Box className="w-4 h-4 text-blue-600" />;
      case "enum": // New case for enum icon
        return <List className="w-4 h-4 text-blue-600" />;
      case "string":
        return <Type className="w-4 h-4 text-blue-600" />;
      case "number":
        return <Hash className="w-4 h-4 text-blue-600" />;
      case "boolean":
        return (
          <i className="w-4 h-4 text-blue-600 font-bold text-xs flex items-center justify-center">
            T/F
          </i>
        );
      case "array":
        return (
          <i className="w-4 h-4 text-blue-600 font-mono text-lg flex items-center justify-center">
            []
          </i>
        );
      default:
        return <Type className="w-4 h-4 text-blue-600" />;
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "object":
        return "bg-purple-100 text-purple-700";
      case "enum": // New case for enum color
        return "bg-blue-100 text-blue-700";
      case "string":
        return "bg-emerald-100 text-emerald-700";
      case "number":
        return "bg-amber-100 text-amber-700";
      case "boolean":
        return "bg-cyan-100 text-cyan-700";
      case "array":
        return "bg-pink-100 text-pink-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const childProps =
    isObject && (propData.propertyOrdering || Object.keys(propData.properties));

  return (
    <div className="relative">
      <div
        className={`flex items-center justify-between w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${isExpandable
          ? "cursor-pointer hover:bg-blue-50/50 hover:shadow-sm"
          : "hover:bg-gray-50/50"
          }`}
        style={{ paddingLeft: `${level * 24 + 16}px` }}
        onClick={() => isExpandable && setIsExpanded(!isExpanded)}
      >
        {level > 0 && (
          <div
            className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-blue-200 to-transparent"
            style={{ left: `${(level - 1) * 24 + 27}px` }}
          />
        )}

        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 shadow-sm">
            {getTypeIcon(displayType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-gray-800 truncate">
                {propName}
              </span>
              {isRequired && (
                <span className="px-2 py-0.5 text-xs font-bold text-rose-700 bg-rose-100 rounded-full border border-rose-200">
                  Required
                </span>
              )}
            </div>
            {propData.description && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {propData.description}
              </p>
            )}
            {hasEnum && (
              <div className="mt-2">
                {/* <p className="text-xs font-semibold text-gray-500 mb-1.5">
                  Enum Values:
                </p> */}
                <div className="flex flex-wrap gap-1.5">
                  {propData.enum.map((enumValue: string | number) => (
                    <span
                      key={String(enumValue)}
                      className="px-2 py-0.5 text-xs font-mono text-sky-800 bg-sky-100 rounded-md border border-sky-200"
                    >
                      {String(enumValue)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <span
            className={`px-2.5 py-1 text-xs font-bold rounded-md ${getTypeBadgeColor(
              displayType // Use the displayType here
            )}`}
          >
            {displayType} {/* And here */}
          </span>
          {isExpandable && (
            <div
              className={`transition-transform duration-200 ${isExpanded ? "" : "-rotate-90"
                }`}
            >
              <ChevronDown className="w-5 h-5 text-blue-400" />
            </div>
          )}
        </div>
      </div>

      {isExpandable && isExpanded && (
        <div className="relative mt-1">
          <div
            className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-blue-200 to-transparent"
            style={{ left: `${level * 24 + 27}px` }}
          />
          {isObject &&
            childProps.map((childPropName: string) => (
              <PropertyView
                key={childPropName}
                propName={childPropName}
                propData={propData.properties[childPropName]}
                isRequired={propData.required?.includes(childPropName) || false}
                level={level + 1}
              />
            ))}
          {isArray && propData.items && (
            <PropertyView
              key={`${propName}-items`}
              propName="items"
              propData={propData.items}
              isRequired={false}
              level={level + 1}
            />
          )}
        </div>
      )}
    </div>
  );
};

// --- Main Template Preview ---
function TemplatePreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<Template | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"schema" | "json">("schema");


  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: "Template list",
      icon: <LayoutTemplate className="w-4 h-4" />,
      path: "/templates",
    },
    {
      label: "View template",
      icon: <Eye className="w-4 h-4" />,
    },
  ];

  useEffect(() => {
    if (!id) {
      toast.error("Invalid template ID");
      navigate("/templates");
      return;
    }

    const fetchTemplate = async () => {
      try {
        const response = await templateService.getTemplateById(id);
        setTemplate(response.data);
      } catch (error) {
        console.error("Error fetching template:", error);
        toast.error("Failed to fetch template details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplate();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-8 text-center h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="bg-white rounded-2xl shadow-xl p-12 border border-blue-100">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-rose-100 to-red-100 flex items-center justify-center">
            <FileJson className="w-10 h-10 text-rose-600" />
          </div>
          <p className="text-2xl font-bold text-gray-800 mb-2">
            Template not found
          </p>
          <p className="text-gray-600 mb-8">
            The template you are looking for does not exist.
          </p>
          <Link
            to="/templates"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Templates
          </Link>
        </div>
      </div>
    );
  }

  const {
    schema_name,
    schema_description,
    createdAt,
    updatedAt,
    output_schema_structure,
  } = template;

  const isRootObject = output_schema_structure?.type === "object";
  const isRootArray = output_schema_structure?.type === "array";

  const propertyOrdering =
    (isRootObject && output_schema_structure.propertyOrdering) ||
    (isRootObject && Object.keys(output_schema_structure.properties || {})) ||
    [];

  const { properties = {}, required = [] } =
    (isRootObject ? output_schema_structure : {}) || {};

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
  <div className="px-6 py-4">

    <Breadcrumbs breadcrumbItems={breadcrumbItems} />

    <div className="px-6 py-4 bg-gradient-to-r from-blue-50 via-blue-50/40 to-slate-50 border-b border-slate-200/60 mt-4">
      <div className="flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />

        <p className="text-sm text-slate-600 leading-relaxed">
          Browse all your existing templates here. You can preview, edit, or
          duplicate any template to create new letters while maintaining
          consistent styles and structure.
        </p>
      </div>
    </div>

  </div>
</div>



      {/* Main Card */}
      <div className="bg-white overflow-hidden mb-6 mt-4 px-6 py-4">
        {/* Header Info */}
        <div className="flex justify-between items-center">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg mt-1">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {schema_name}
              </h2>
              <p className="text-sm text-slate-600">
                {schema_description}
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all">
              <Calendar className="w-6 h-6 text-blue-500" />
              <div>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Created On
                </dt>
                <dd className="text-sm font-medium text-gray-900 mt-1">
                  {formatDate(createdAt)}
                </dd>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all">
              <Clock className="w-6 h-6 text-green-500" />
              <div>
                <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Last Updated
                </dt>
                <dd className="text-sm font-medium text-gray-900 mt-1">
                  {formatDate(updatedAt)}
                </dd>
              </div>
            </div>
          </dl>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex gap-2 border-gray-200">
            <button
              onClick={() => setActiveTab("schema")}
              className={`flex items-center gap-2 px-4 py-3 font-semibold transition-all duration-200 relative -mb-px ${activeTab === "schema"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-blue-600 border-b-2 border-transparent"
                }`}
            >
              <List className="w-4 h-4" />
              Schema Structure
            </button>
            <button
              onClick={() => setActiveTab("json")}
              className={`flex items-center gap-2 px-4 py-3 font-semibold transition-all duration-200 relative -mb-px ${activeTab === "json"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-blue-600 border-b-2 border-transparent"
                }`}
            >
              <Code className="w-4 h-4" />
              Raw JSON
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "schema" ? (
            isRootObject && propertyOrdering.length > 0 ? (
              <div className="space-y-2 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-4 border border-gray-200">
                {propertyOrdering.map((propName) => (
                  <PropertyView
                    key={propName}
                    propName={propName}
                    propData={properties[propName]}
                    isRequired={required.includes(propName)}
                  />
                ))}
              </div>
            ) :
              isRootArray ? (
                <div className="space-y-2 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-4 border border-gray-200">
                  <PropertyView
                    key="root-array"
                    propName={schema_name || "Root Array"}
                    propData={output_schema_structure}
                    isRequired={false}
                  />
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-blue-100 flex items-center justify-center">
                    <Box className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-semibold text-lg">
                    No properties defined in this schema
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Add properties to see the schema structure
                  </p>
                </div>
              )
          ) : (
            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <textarea
                value={JSON.stringify(output_schema_structure, null, 2)}
                readOnly
                className="w-full h-96 bg-transparent text-green-400 text-sm font-mono resize-none focus:outline-none"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplatePreview;